import {
  colorLineByRoads,
  type ColoredSpan,
  type RoadBand,
  type RoadSpatialIndex,
} from "./editor-geom.ts";
import { isDecorative } from "./decorative-segments.ts";
import { lineStyleKey, type LineStyle, type PointStyle } from "./feature-style.ts";
import { fullySelectedSegments } from "./lasso.ts";
import { markerIndices } from "./point-markers.ts";
import { resolveStart } from "./route-graph.ts";
import { isFullPointSelection, type SelectAllStep } from "./selection.ts";
import type { LngLat } from "./types.ts";

type RoadSegment = readonly [LngLat, LngLat];
type PointRole = "selected" | "start" | "vertex";

export interface DraftInput {
  points: readonly LngLat[];
  segments: ReadonlyArray<readonly [number, number]>;
  selection: readonly number[];
  selectedLines: readonly number[];
  showPoints: boolean;
  start: number | null;
  /**
   * Select-filter highlight. "points" keeps lines off even when both ends
   * are selected. "lines" turns every line on. Omitted uses the usual rule:
   * a line lights up when both ends are selected, or when it is listed.
   */
  selectAll?: SelectAllStep | null;
  /**
   * Old point index → new index, or -1 when that point was removed.
   * Merge passes this so a moved survivor does not force a full recolor.
   */
  pointMap?: Int32Array | null;
  /** Parallel to points. Null keeps the role color and size. */
  pointStyles?: readonly (PointStyle | null)[];
  /** Parallel to segments. Null keeps the road-band color and a solid stroke. */
  lineStyles?: readonly (LineStyle | null)[];
  /** Parallel to segments. True marks a decorative segment (장식선). */
  decorative?: readonly boolean[];
  /** When true, unselected decorative lines stay visible while drawing 장식선. */
  decorativeStroke?: boolean;
}

export interface DraftFeature {
  id: number;
  type: "Feature";
  properties:
    | {
        band: RoadBand;
        picked: 0 | 1;
        decorative?: 0 | 1;
        strokePreview?: 0 | 1;
        stroke?: string;
        width?: number;
        pattern?: LineStyle["pattern"];
      }
    | { role: PointRole; color?: string; radius?: number };
  geometry:
    | { type: "LineString"; coordinates: LngLat[] }
    | { type: "Point"; coordinates: LngLat };
}

export interface DraftFeatureDiff {
  id: number;
  newGeometry?: DraftFeature["geometry"];
  addOrUpdateProperties?: Array<{ key: string; value: string | number }>;
  removeProperties?: string[];
}

/** A MapLibre GeoJSONSourceDiff. Empty lists are omitted. */
export interface DraftDiff {
  remove?: number[];
  add?: DraftFeature[];
  update?: DraftFeatureDiff[];
}

export function mergeDraftDiffs(first: DraftDiff | null, second: DraftDiff | null): DraftDiff | null {
  if (!first) return second;
  if (!second) return first;
  const diff: DraftDiff = {};
  const remove = [...(first.remove ?? []), ...(second.remove ?? [])];
  const add = [...(first.add ?? []), ...(second.add ?? [])];
  const update = [...(first.update ?? []), ...(second.update ?? [])];
  if (remove.length > 0) diff.remove = remove;
  if (add.length > 0) diff.add = add;
  if (update.length > 0) diff.update = update;
  return diff;
}

export type DraftChange = {
  kind: "set" | "update" | "none";
  features?: DraftFeature[];
  diff?: DraftDiff;
  offRoad: boolean;
  /** Segments whose road color was computed this call. Cache hits stay at 0. */
  colored: number;
};

interface LineRec {
  ids: number[];
  key: string;
  picked: 0 | 1;
  far: number;
  styleKey: string;
  decorative: boolean;
  strokePreview: boolean;
}

/**
 * Colored spans for each segment, reused until its endpoints or the work-area
 * roads change. Selection only flips `picked` / point role. The map receives
 * a feature diff, except for the first paint and a road-cache reset.
 */
export class DraftScene {
  private roads: readonly RoadSegment[] = [];
  private index: RoadSpatialIndex | null = null;
  private roadsDirty = false;
  private spans = new Map<string, ColoredSpan[]>();
  private features = new Map<number, DraftFeature>();
  private lines: LineRec[] = [];
  private pointIds: number[] = [];
  private pointShown: boolean[] = [];
  private pointRoles: PointRole[] = [];
  private prevPoints: LngLat[] = [];
  private prevSegments: Array<[number, number]> = [];
  /** Feature objects last sent to the map. Identity tells a rewrite from a retint. */
  private published = new Map<number, DraftFeature>();
  private nextId = 1;
  private far = 0;
  private hasState = false;

  /** Drop every cached span. The next paint recomputes colors from these roads. */
  replaceRoads(roads: readonly RoadSegment[], index: RoadSpatialIndex | null): void {
    this.roads = roads;
    this.index = index;
    this.spans.clear();
    this.roadsDirty = true;
  }

  apply(input: DraftInput): DraftChange {
    if (!this.hasState || this.roadsDirty) return this.rebuild(input);
    if (this.sameTopology(input)) return this.applySameTopology(input);
    const pointMap = usablePointMap(input.pointMap, this.prevPoints.length, input.points.length) ?? matchPoints(this.prevPoints, input.points);
    if (!pointMap) return this.rebuild(input);
    return this.applyRelinked(input, pointMap);
  }

  /**
   * Hide the lines a brush sample just hit, plus points that no longer touch
   * a line. Indexes stay put until the stroke commits and `apply` compacts them.
   */
  dropSegments(segmentIndexes: readonly number[], orphanPoints: readonly number[]): DraftDiff | null {
    if (!this.hasState) return null;
    const diff = new Bucket();
    for (const index of segmentIndexes) {
      const line = this.lines[index];
      if (!line || line.ids.length === 0) continue;
      this.dropLine(line, diff);
      for (const id of line.ids) this.published.delete(id);
      this.lines[index] = { ids: [], key: "", picked: 0, far: 0, styleKey: "", decorative: false, strokePreview: false };
    }
    for (const index of orphanPoints) {
      if (!this.pointShown[index]) continue;
      const id = this.pointIds[index];
      if (id == null) continue;
      this.features.delete(id);
      this.published.delete(id);
      diff.remove.push(id);
      this.pointShown[index] = false;
    }
    if (diff.empty()) return null;
    this.recountFar();
    return diff.toDiff();
  }

  /** Drop the selection tint on lines that are still drawn. */
  unpickLines(indexes: readonly number[]): DraftDiff | null {
    if (!this.hasState) return null;
    const diff = new Bucket();
    for (const index of indexes) {
      const line = this.lines[index];
      if (!line || line.ids.length === 0 || line.picked === 0) continue;
      this.retint(line, 0, diff);
      for (const id of line.ids) {
        const feature = this.features.get(id);
        if (feature) this.published.set(id, feature);
      }
      this.lines[index] = { ...line, picked: 0 };
    }
    if (diff.empty()) return null;
    return diff.toDiff();
  }

  /** Features in segment order, then visible points in index order. */
  materialize(): DraftFeature[] {
    const out: DraftFeature[] = [];
    for (const line of this.lines) {
      for (const id of line.ids) {
        const feature = this.features.get(id);
        if (feature) out.push(feature);
      }
    }
    for (let i = 0; i < this.pointIds.length; i++) {
      if (!this.pointShown[i]) continue;
      const feature = this.features.get(this.pointIds[i]);
      if (feature) out.push(feature);
    }
    return out;
  }

  private rebuild(input: DraftInput): DraftChange {
    if (this.roadsDirty) this.spans.clear();
    this.roadsDirty = false;
    this.features.clear();
    this.lines = [];
    this.pointIds = [];
    this.pointShown = [];
    this.pointRoles = [];
    this.nextId = 1;
    this.far = 0;
    let colored = 0;
    const highlight = lineHighlight(input);
    for (let i = 0; i < input.segments.length; i++) {
      const rec = this.paintLine(input, i, null, wantLine(highlight, i));
      if (rec.fresh) colored += 1;
      this.lines.push(rec.line);
      this.far += rec.line.far;
    }
    const resolved = resolvedStart(input);
    const selected = new Set(input.selection);
    const visible = new Set(markerIndices(input.points.length, input.selection, input.showPoints));
    for (let i = 0; i < input.points.length; i++) {
      const at = input.points[i];
      const id = this.nextId++;
      const role = pointRole(i, selected, resolved);
      const show = visible.has(i) && !!at;
      this.pointIds.push(id);
      this.pointShown.push(show);
      this.pointRoles.push(role);
      if (!show || !at) continue;
      const feature = pointFeature(id, at, role, pointStyleAt(input, i));
      this.features.set(id, feature);
    }
    this.remember(input);
    return { kind: "set", features: this.materialize(), offRoad: this.far > 0, colored };
  }

  /** Drag, selection, and a new point at the end keep every previous segment index. */
  private sameTopology(input: DraftInput): boolean {
    if (this.prevPoints.length !== input.points.length) return false;
    if (this.prevSegments.length !== input.segments.length) return false;
    for (let i = 0; i < input.segments.length; i++) {
      const prev = this.prevSegments[i];
      const next = input.segments[i];
      if (!prev || !next || prev[0] !== next[0] || prev[1] !== next[1]) return false;
    }
    return true;
  }

  private applySameTopology(input: DraftInput): DraftChange {
    const diff = new Bucket();
    const highlight = lineHighlight(input);
    const moved = movedPoints(this.prevPoints, input.points);
    let colored = 0;
    let painted = false;
    const nextLines: LineRec[] = new Array(input.segments.length);
    for (let i = 0; i < input.segments.length; i++) {
      const pair = input.segments[i];
      const prev = this.lines[i];
      const want = wantLine(highlight, i);
      const touches = !!pair && (moved.has(pair[0]) || moved.has(pair[1]));
      if (prev && prev.key && !touches) {
      const style = lineStyleAt(input, i);
      const decorative = decorativeAt(input, i);
      const strokePreview = strokePreviewAt(input, decorative, want);
      if (
        prev.picked !== want ||
        prev.styleKey !== lineStyleKey(style) ||
        prev.decorative !== decorative ||
        prev.strokePreview !== strokePreview
      ) {
        nextLines[i] = this.patchLine(prev, want, style, decorative, strokePreview, diff);
      } else {
        nextLines[i] = prev;
      }
      continue;
    }
    painted = true;
    const rec = this.paintLine(input, i, prev ?? null, want);
      if (rec.fresh) colored += 1;
      nextLines[i] = rec.line;
    }
    this.lines = nextLines;
    this.recountFar();
    this.syncPoints(input, identityMap(input.points.length), [], diff);
    this.gcSpans();
    return this.finish(input, diff, colored, !painted && moved.size === 0);
  }

  private applyRelinked(input: DraftInput, pointMap: Int32Array): DraftChange {
    const diff = new Bucket();
    const linked = matchSegments(this.prevSegments, input.segments, pointMap);
    const highlight = lineHighlight(input);
    let colored = 0;
    const nextLines: LineRec[] = new Array(input.segments.length);
    for (let i = 0; i < input.segments.length; i++) {
      const oldI = linked.newToOld[i] ?? -1;
      const prev = oldI >= 0 ? this.lines[oldI] : null;
      const want = wantLine(highlight, i);
      const pair = input.segments[i];
      const pa = pair ? input.points[pair[0]] : undefined;
      const pb = pair ? input.points[pair[1]] : undefined;
      const key = pa && pb ? coordKey(pa, pb) : "";
      if (prev && prev.key && prev.key === key) {
        const style = lineStyleAt(input, i);
        const decorative = decorativeAt(input, i);
        const strokePreview = strokePreviewAt(input, decorative, want);
        if (
          prev.picked !== want ||
          prev.styleKey !== lineStyleKey(style) ||
          prev.decorative !== decorative ||
          prev.strokePreview !== strokePreview
        ) {
          nextLines[i] = this.patchLine(prev, want, style, decorative, strokePreview, diff);
        } else {
          nextLines[i] = prev;
        }
        continue;
      }
      const rec = this.paintLine(input, i, prev, want);
      if (rec.fresh) colored += 1;
      nextLines[i] = rec.line;
    }
    for (let oldI = 0; oldI < this.lines.length; oldI++) {
      if (linked.oldUsed[oldI]) continue;
      this.dropLine(this.lines[oldI], diff);
    }
    this.lines = nextLines;
    this.recountFar();
    const oldForNew = new Int32Array(input.points.length).fill(-1);
    const removed: number[] = [];
    for (let oldI = 0; oldI < pointMap.length; oldI++) {
      const next = pointMap[oldI] ?? -1;
      if (next >= 0) oldForNew[next] = oldI;
      else removed.push(oldI);
    }
    this.syncPoints(input, oldForNew, removed, diff);
    this.gcSpans();
    return this.finish(input, diff, colored, false);
  }

  private paintLine(
    input: DraftInput,
    index: number,
    prev: LineRec | null,
    picked: 0 | 1,
  ): { line: LineRec; fresh: boolean } {
    const pair = input.segments[index];
    const pa = pair ? input.points[pair[0]] : undefined;
    const pb = pair ? input.points[pair[1]] : undefined;
    if (!pair || !pa || !pb || pair[0] === pair[1]) {
      if (prev) this.dropIds(prev.ids);
      return { line: { ids: [], key: "", picked: 0, far: 0, styleKey: "", decorative: false, strokePreview: false }, fresh: false };
    }
    const key = coordKey(pa, pb);
    const hit = this.spans.get(key);
    const spans = hit ?? this.makeSpans(pa, pb);
    if (!hit) this.spans.set(key, spans);
    const decorative = decorativeAt(input, index);
    const line = this.writeLine(
      prev,
      spans,
      picked,
      key,
      lineStyleAt(input, index),
      decorative,
      strokePreviewAt(input, decorative, picked),
    );
    return { line, fresh: !hit };
  }

  private makeSpans(a: LngLat, b: LngLat): ColoredSpan[] {
    if (this.roads.length === 0) {
      return [{ coordinates: [[a[0], a[1]], [b[0], b[1]]], band: "plain" }];
    }
    return colorLineByRoads([a, b], this.roads, this.index);
  }

  /** Keep the same feature ids when the span count does not change. */
  private writeLine(
    prev: LineRec | null,
    spans: ColoredSpan[],
    picked: 0 | 1,
    key: string,
    style: LineStyle | null,
    decorative: boolean,
    strokePreview: boolean,
  ): LineRec {
    const far = decorative ? 0 : farCount(spans);
    const styleKey = lineStyleKey(style);
    if (prev && prev.ids.length === spans.length && prev.ids.length > 0) {
      for (let s = 0; s < spans.length; s++) {
        const id = prev.ids[s];
        const span = spans[s];
        if (id == null || !span) continue;
        const feature = lineFeature(id, span, picked, style, decorative, strokePreview);
        this.features.set(id, feature);
      }
      return { ids: prev.ids, key, picked, far, styleKey, decorative, strokePreview };
    }
    if (prev) this.dropIds(prev.ids);
    const ids = spans.map(() => this.nextId++);
    for (let s = 0; s < spans.length; s++) {
      const id = ids[s];
      const span = spans[s];
      if (id == null || !span) continue;
      this.features.set(id, lineFeature(id, span, picked, style, decorative, strokePreview));
    }
    return { ids, key, picked, far, styleKey, decorative, strokePreview };
  }

  /** Selection tint and stroke style, without rebuilding the span geometry. */
  private patchLine(
    prev: LineRec,
    picked: 0 | 1,
    style: LineStyle | null,
    decorative: boolean,
    strokePreview: boolean,
    diff: Bucket,
  ): LineRec {
    const styleKey = lineStyleKey(style);
    if (
      prev.picked === picked &&
      prev.styleKey === styleKey &&
      prev.decorative === decorative &&
      prev.strokePreview === strokePreview
    ) {
      return prev;
    }
    for (const id of prev.ids) {
      const feature = this.features.get(id);
      if (!feature || feature.geometry.type !== "LineString" || !("picked" in feature.properties)) continue;
      const add: Array<{ key: string; value: string | number }> = [];
      const remove: string[] = [];
      if (feature.properties.picked !== picked) {
        feature.properties.picked = picked;
        add.push({ key: "picked", value: picked });
      }
      writeLineStyle(feature.properties, style, add, remove);
      if ("band" in feature.properties) {
        const wantDecorative = decorative ? 1 : 0;
        const has = feature.properties.decorative === 1 ? 1 : 0;
        if (has !== wantDecorative) {
          if (wantDecorative) feature.properties.decorative = 1;
          else delete feature.properties.decorative;
          add.push({ key: "decorative", value: wantDecorative });
        }
        const wantPreview = strokePreview ? 1 : 0;
        const hasPreview = feature.properties.strokePreview === 1 ? 1 : 0;
        if (hasPreview !== wantPreview) {
          if (wantPreview) feature.properties.strokePreview = 1;
          else delete feature.properties.strokePreview;
          add.push({ key: "strokePreview", value: wantPreview });
        }
      }
      if (add.length === 0 && remove.length === 0) continue;
      const update: DraftFeatureDiff = { id };
      if (add.length > 0) update.addOrUpdateProperties = add;
      if (remove.length > 0) update.removeProperties = remove;
      diff.update.push(update);
    }
    return { ...prev, picked, styleKey, decorative, strokePreview, far: decorative ? 0 : prev.far };
  }

  private retint(prev: LineRec, picked: 0 | 1, diff: Bucket): void {
    for (const id of prev.ids) {
      if (id == null) continue;
      const feature = this.features.get(id);
      if (!feature || feature.geometry.type !== "LineString" || !("picked" in feature.properties)) continue;
      if (feature.properties.picked === picked) continue;
      feature.properties.picked = picked;
      diff.update.push({ id, addOrUpdateProperties: [{ key: "picked", value: picked }] });
    }
  }

  private dropLine(prev: LineRec | undefined, diff: Bucket): void {
    if (!prev) return;
    for (const id of prev.ids) {
      this.features.delete(id);
      diff.remove.push(id);
    }
  }

  private dropIds(ids: readonly number[]): void {
    for (const id of ids) this.features.delete(id);
  }

  private syncPoints(input: DraftInput, oldForNew: Int32Array, removedOld: readonly number[], diff: Bucket): void {
    const resolved = resolvedStart(input);
    const allSelected = isFullPointSelection(input.selection, input.points.length);
    const selected = allSelected ? null : new Set(input.selection);
    const showAll = input.showPoints;
    const visible = showAll ? null : new Set(markerIndices(input.points.length, input.selection, false));
    const nextIds: number[] = new Array(input.points.length);
    const nextShown: boolean[] = new Array(input.points.length);
    const nextRoles: PointRole[] = new Array(input.points.length);
    for (let i = 0; i < input.points.length; i++) {
      const oldI = oldForNew[i] ?? -1;
      const at = input.points[i];
      const role = pointRole(i, selected, resolved, allSelected);
      const style = pointStyleAt(input, i);
      const show = (showAll || visible?.has(i) === true) && !!at;
      const id = oldI >= 0 ? this.pointIds[oldI] : this.nextId++;
      if (id == null) continue;
      nextIds[i] = id;
      nextShown[i] = show;
      nextRoles[i] = role;
      const wasShown = oldI >= 0 && this.pointShown[oldI] === true;
      const prevRole = oldI >= 0 ? this.pointRoles[oldI] : undefined;
      const prevAt = oldI >= 0 ? this.prevPoints[oldI] : undefined;
      const moved = !!at && !!prevAt && (prevAt[0] !== at[0] || prevAt[1] !== at[1]);
      if (wasShown && !show) {
        this.features.delete(id);
        diff.remove.push(id);
        continue;
      }
      if (!show || !at) continue;
      if (!wasShown) {
        const feature = pointFeature(id, at, role, style);
        this.features.set(id, feature);
        diff.add.push(feature);
        continue;
      }
      const existing = this.features.get(id);
      const styleSame = pointStyleMatches(existing, style);
      if (!moved && prevRole === role && styleSame) continue;
      if (!moved && existing && existing.geometry.type === "Point" && "role" in existing.properties) {
        const add: Array<{ key: string; value: string | number }> = [];
        const remove: string[] = [];
        if (prevRole !== role) {
          existing.properties.role = role;
          add.push({ key: "role", value: role });
        }
        writePointStyle(existing.properties, style, add, remove);
        if (add.length > 0 || remove.length > 0) {
          const update: DraftFeatureDiff = { id };
          if (add.length > 0) update.addOrUpdateProperties = add;
          if (remove.length > 0) update.removeProperties = remove;
          diff.update.push(update);
        }
        continue;
      }
      const feature = pointFeature(id, at, role, style);
      this.features.set(id, feature);
      const update: DraftFeatureDiff = { id, newGeometry: feature.geometry };
      const add: Array<{ key: string; value: string | number }> = [];
      const remove: string[] = [];
      if (prevRole !== role) add.push({ key: "role", value: role });
      if (existing && existing.geometry.type === "Point" && "role" in existing.properties) {
        writePointStyle(existing.properties, style, add, remove);
      }
      if (add.length > 0) update.addOrUpdateProperties = add;
      if (remove.length > 0) update.removeProperties = remove;
      diff.update.push(update);
    }
    for (const oldI of removedOld) {
      if (!this.pointShown[oldI]) continue;
      const id = this.pointIds[oldI];
      if (id == null) continue;
      this.features.delete(id);
      diff.remove.push(id);
    }
    this.pointIds = nextIds;
    this.pointShown = nextShown;
    this.pointRoles = nextRoles;
  }

  /** Queue line rewrites, then snapshot the features that the diff publishes. */
  private finish(input: DraftInput, diff: Bucket, colored: number, reuseSnapshot: boolean): DraftChange {
    this.queueLineEdits(diff);
    const structural = diff.add.length > 0 || diff.remove.length > 0;
    this.remember(input, reuseSnapshot && !structural);
    if (diff.empty()) return { kind: "none", offRoad: this.far > 0, colored };
    return { kind: "update", diff: diff.toDiff(), offRoad: this.far > 0, colored };
  }

  /**
   * paintLine writes features in place. A new object is an add or a geometry
   * update; a retint keeps the same object and already recorded the property.
   */
  private queueLineEdits(diff: Bucket): void {
    const queued = new Set<number>();
    for (const update of diff.update) queued.add(update.id);
    for (const feature of diff.add) queued.add(feature.id);
    for (const id of diff.remove) queued.add(id);
    const live = new Set<number>();
    for (const line of this.lines) {
      for (const id of line.ids) live.add(id);
    }
    for (const [id, feature] of this.published) {
      if (live.has(id) || queued.has(id) || feature.geometry.type !== "LineString") continue;
      diff.remove.push(id);
      queued.add(id);
    }
    for (const line of this.lines) {
      for (const id of line.ids) {
        if (queued.has(id)) continue;
        const feature = this.features.get(id);
        if (!feature || feature.geometry.type !== "LineString") continue;
        const previous = this.published.get(id);
        if (!previous) {
          diff.add.push(feature);
          continue;
        }
        if (previous === feature) continue;
        const patch = linePropertyPatch(previous, feature, line.picked);
        const update: DraftFeatureDiff = {
          id,
          newGeometry: feature.geometry,
          addOrUpdateProperties: patch.add,
        };
        if (patch.remove.length > 0) update.removeProperties = patch.remove;
        diff.update.push(update);
      }
    }
  }

  private recountFar(): void {
    let far = 0;
    for (const line of this.lines) far += line.far;
    this.far = far;
  }

  private remember(input: DraftInput, reuseSnapshot = false): void {
    if (!reuseSnapshot) {
      this.prevPoints = input.points.map((point) => [point[0], point[1]]);
      this.prevSegments = input.segments.map((segment) => [segment[0], segment[1]]);
      this.published = new Map(this.features);
    }
    this.hasState = true;
  }

  private gcSpans(): void {
    if (this.spans.size <= this.lines.length + 32) return;
    const used = new Set<string>();
    for (const line of this.lines) {
      if (line.key) used.add(line.key);
    }
    for (const key of this.spans.keys()) {
      if (!used.has(key)) this.spans.delete(key);
    }
  }
}

function resolvedStart(input: DraftInput): number | null {
  if (input.points.length === 0) return null;
  return resolveStart({
    points: input.points as LngLat[],
    segments: input.segments as Array<[number, number]>,
    start: input.start,
  });
}

function lineHighlight(input: DraftInput): { mode: "all" | "points" | "lines" | "set"; picked: Set<number> | null } {
  if (input.selectAll === "points") return { mode: "points", picked: null };
  if (input.selectAll === "lines") return { mode: "lines", picked: null };
  const allPoints = input.selectedLines.length === 0 && isFullPointSelection(input.selection, input.points.length);
  if (allPoints) return { mode: "all", picked: null };
  return { mode: "set", picked: pickedSegments(input) };
}

function wantLine(highlight: { mode: "all" | "points" | "lines" | "set"; picked: Set<number> | null }, index: number): 0 | 1 {
  if (highlight.mode === "points") return 0;
  if (highlight.mode === "all" || highlight.mode === "lines") return 1;
  return highlight.picked?.has(index) ? 1 : 0;
}

function pickedSegments(input: DraftInput): Set<number> {
  const picked = new Set(fullySelectedSegments(input.segments, input.selection));
  for (const index of input.selectedLines) {
    if (index >= 0 && index < input.segments.length) picked.add(index);
  }
  return picked;
}

function pointRole(
  index: number,
  selected: ReadonlySet<number> | null,
  start: number | null,
  allSelected = false,
): PointRole {
  if (allSelected || selected?.has(index)) return "selected";
  if (index === start) return "start";
  return "vertex";
}

function movedPoints(prev: readonly LngLat[], next: readonly LngLat[]): Set<number> {
  const moved = new Set<number>();
  const count = Math.min(prev.length, next.length);
  for (let i = 0; i < count; i++) {
    const a = prev[i];
    const b = next[i];
    if (!a || !b || a[0] !== b[0] || a[1] !== b[1]) moved.add(i);
  }
  return moved;
}

function identityMap(count: number): Int32Array {
  const map = new Int32Array(count);
  for (let i = 0; i < count; i++) map[i] = i;
  return map;
}

/** A caller-supplied map is used only when it covers every new point exactly. */
function usablePointMap(map: Int32Array | null | undefined, prevCount: number, nextCount: number): Int32Array | null {
  if (!map || map.length !== prevCount || nextCount < 0) return null;
  const seen = new Uint8Array(nextCount);
  let covered = 0;
  for (let i = 0; i < map.length; i++) {
    const next = map[i] ?? -1;
    if (next === -1) continue;
    if (next < 0 || next >= nextCount || seen[next] === 1) return null;
    seen[next] = 1;
    covered += 1;
  }
  return covered === nextCount ? map : null;
}

function matchPoints(prev: readonly LngLat[], next: readonly LngLat[]): Int32Array | null {
  if (prev.length === next.length) return identityMap(prev.length);
  if (next.length > prev.length) {
    for (let i = 0; i < prev.length; i++) {
      const a = prev[i];
      const b = next[i];
      if (!a || !b || a[0] !== b[0] || a[1] !== b[1]) return null;
    }
    return identityMap(prev.length);
  }
  const map = new Int32Array(prev.length).fill(-1);
  let nextIndex = 0;
  for (let i = 0; i < prev.length && nextIndex < next.length; i++) {
    const a = prev[i];
    const b = next[nextIndex];
    if (!a || !b) return null;
    if (a[0] === b[0] && a[1] === b[1]) {
      map[i] = nextIndex;
      nextIndex += 1;
    }
  }
  if (nextIndex !== next.length) return null;
  return map;
}

function matchSegments(
  prev: ReadonlyArray<readonly [number, number]>,
  next: ReadonlyArray<readonly [number, number]>,
  pointMap: Int32Array,
): { newToOld: Int32Array; oldUsed: Uint8Array } {
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < prev.length; i++) {
    const pair = prev[i];
    if (!pair) continue;
    const a = pointMap[pair[0]] ?? -1;
    const b = pointMap[pair[1]] ?? -1;
    if (a < 0 || b < 0) continue;
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    const list = buckets.get(key);
    if (list) list.push(i);
    else buckets.set(key, [i]);
  }
  const newToOld = new Int32Array(next.length).fill(-1);
  const oldUsed = new Uint8Array(prev.length);
  for (let i = 0; i < next.length; i++) {
    const pair = next[i];
    if (!pair) continue;
    const key = pair[0] < pair[1] ? `${pair[0]}:${pair[1]}` : `${pair[1]}:${pair[0]}`;
    const list = buckets.get(key);
    const oldI = list?.pop();
    if (oldI == null) continue;
    newToOld[i] = oldI;
    oldUsed[oldI] = 1;
  }
  return { newToOld, oldUsed };
}

function coordKey(a: LngLat, b: LngLat): string {
  let a0 = a[0];
  let a1 = a[1];
  let b0 = b[0];
  let b1 = b[1];
  if (b0 < a0 || (b0 === a0 && b1 < a1)) {
    const swap0 = a0;
    const swap1 = a1;
    a0 = b0;
    a1 = b1;
    b0 = swap0;
    b1 = swap1;
  }
  return `${a0},${a1}|${b0},${b1}`;
}

function farCount(spans: readonly ColoredSpan[]): number {
  let count = 0;
  for (const span of spans) if (span.band === "far") count += 1;
  return count;
}

function bandOf(feature: DraftFeature): RoadBand {
  return "band" in feature.properties ? feature.properties.band : "plain";
}

function decorativeAt(input: DraftInput, index: number): boolean {
  return isDecorative(input.decorative, index);
}

function strokePreviewAt(input: DraftInput, decorative: boolean, picked: 0 | 1): boolean {
  return decorative && picked === 0 && input.decorativeStroke === true;
}

function lineFeature(
  id: number,
  span: ColoredSpan,
  picked: 0 | 1,
  style: LineStyle | null,
  decorative: boolean,
  strokePreview: boolean,
): DraftFeature {
  const properties: DraftFeature["properties"] = { band: span.band, picked };
  if (decorative && "band" in properties) properties.decorative = 1;
  if (strokePreview && "band" in properties) properties.strokePreview = 1;
  if (style && "band" in properties) {
    properties.stroke = style.color;
    properties.width = style.width;
    properties.pattern = style.pattern;
  }
  return {
    id,
    type: "Feature",
    properties,
    geometry: { type: "LineString", coordinates: span.coordinates },
  };
}

function pointFeature(id: number, at: LngLat, role: PointRole, style: PointStyle | null): DraftFeature {
  const properties: DraftFeature["properties"] = { role };
  if (style && "role" in properties) {
    properties.color = style.color;
    properties.radius = style.size;
  }
  return {
    id,
    type: "Feature",
    properties,
    geometry: { type: "Point", coordinates: [at[0], at[1]] },
  };
}

function pointStyleAt(input: DraftInput, index: number): PointStyle | null {
  return input.pointStyles?.[index] ?? null;
}

function lineStyleAt(input: DraftInput, index: number): LineStyle | null {
  return input.lineStyles?.[index] ?? null;
}

export { decorativeAt };

function pointStyleMatches(feature: DraftFeature | undefined, style: PointStyle | null): boolean {
  if (!feature || feature.geometry.type !== "Point" || !("role" in feature.properties)) return style == null;
  const props = feature.properties;
  if (!style) return props.color == null && props.radius == null;
  return props.color === style.color && props.radius === style.size;
}

function writePointStyle(
  props: { color?: string; radius?: number },
  style: PointStyle | null,
  add: Array<{ key: string; value: string | number }>,
  remove: string[],
): void {
  if (style) {
    if (props.color !== style.color) {
      props.color = style.color;
      add.push({ key: "color", value: style.color });
    }
    if (props.radius !== style.size) {
      props.radius = style.size;
      add.push({ key: "radius", value: style.size });
    }
    return;
  }
  if (props.color != null) {
    delete props.color;
    remove.push("color");
  }
  if (props.radius != null) {
    delete props.radius;
    remove.push("radius");
  }
}

function writeLineStyle(
  props: { stroke?: string; width?: number; pattern?: LineStyle["pattern"] },
  style: LineStyle | null,
  add: Array<{ key: string; value: string | number }>,
  remove: string[],
): void {
  if (style) {
    if (props.stroke !== style.color) {
      props.stroke = style.color;
      add.push({ key: "stroke", value: style.color });
    }
    if (props.width !== style.width) {
      props.width = style.width;
      add.push({ key: "width", value: style.width });
    }
    if (props.pattern !== style.pattern) {
      props.pattern = style.pattern;
      add.push({ key: "pattern", value: style.pattern });
    }
    return;
  }
  if (props.stroke != null) {
    delete props.stroke;
    remove.push("stroke");
  }
  if (props.width != null) {
    delete props.width;
    remove.push("width");
  }
  if (props.pattern != null) {
    delete props.pattern;
    remove.push("pattern");
  }
}

function linePropertyPatch(
  previous: DraftFeature | undefined,
  feature: DraftFeature,
  picked: 0 | 1,
): { add: Array<{ key: string; value: string | number }>; remove: string[] } {
  const add: Array<{ key: string; value: string | number }> = [
    { key: "band", value: bandOf(feature) },
    { key: "picked", value: picked },
  ];
  const remove: string[] = [];
  const next = "band" in feature.properties ? feature.properties : null;
  const prev = previous && "band" in previous.properties ? previous.properties : null;
  pushStyleProp(add, remove, "stroke", next?.stroke, prev?.stroke);
  pushStyleProp(add, remove, "width", next?.width, prev?.width);
  pushStyleProp(add, remove, "pattern", next?.pattern, prev?.pattern);
  return { add, remove };
}

function pushStyleProp(
  add: Array<{ key: string; value: string | number }>,
  remove: string[],
  key: string,
  next: string | number | undefined,
  prev: string | number | undefined,
): void {
  if (next != null) {
    if (next !== prev) add.push({ key, value: next });
    return;
  }
  if (prev != null) remove.push(key);
}

/** Collects a source diff. Line geometry writes are queued once in finish(). */
class Bucket {
  readonly remove: number[] = [];
  readonly add: DraftFeature[] = [];
  readonly update: DraftFeatureDiff[] = [];

  empty(): boolean {
    return this.remove.length === 0 && this.add.length === 0 && this.update.length === 0;
  }

  toDiff(): DraftDiff {
    const diff: DraftDiff = {};
    if (this.remove.length > 0) diff.remove = this.remove;
    if (this.add.length > 0) diff.add = this.add;
    if (this.update.length > 0) diff.update = this.update;
    return diff;
  }
}
